import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { InputNumberMask } from '../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Separator } from '../../../../../../../CommonComponents/OPL/Separator/Separator';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../../../CommonUtils/formatNumber';
import { RetirementExpenses } from '../../../../../../../ProfileOPL/Goals/goals/Retirement/Retirement.meta';
import { LivingExpensesChangeOptions } from '../../../../api/useWhatIfOptionsQuery';
import { FormValues } from './LivingExpensesChange.meta';
import { useLivingExpensesPlanImpactPreview } from './useLivingExpensesPlanImpactPreview';

interface Props {
  Title: string;
  options: LivingExpensesChangeOptions;
  clientId: number;
  fpId: string;
  goalId?: string;
}

export const LivingExpensesChange = ({ Title, options, clientId, fpId, goalId }: Props) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<FormValues>();

  const selectedPercent = watch('PercentOfLivingExpenses');
  const livingExpenseAmount = watch('LivingExpenseAmount');
  const showAmountInput = selectedPercent === RetirementExpenses.Other;

  // Endpoints other than PlanImpact/ApplyToPlan do not run the server-side percent-to-amount
  // resolution, so preview payloads have to carry the absolute yearly target themselves. The
  // per-option amounts are computed by the backend, so this only picks the right one.
  const resolvedLivingExpenseAmount = showAmountInput
    ? livingExpenseAmount
    : options.LivingExpensesOptions.find(opt => opt.value === selectedPercent)?.amount ?? null;

  useLivingExpensesPlanImpactPreview({
    clientId,
    fpId,
    goalId,
    percentOfLivingExpenses: selectedPercent,
    resolvedLivingExpenseAmount,
  });

  const livingExpensesOptions = options.LivingExpensesOptions.map(opt => ({
    label: opt.label,
    value: opt.value,
    rightLabel: opt.amount == null ? undefined : formatCurrency(opt.amount),
  }));

  return (
    <>
      <Text fontSize="h5" fontWeight={600} px="12" py="16">
        What-if...
      </Text>

      <Separator />

      <Box display="flex" flexDirection="column" p={12} style={{ gap: 16 }}>
        <Text fontSize="h6" fontWeight={500}>
          {Title}
        </Text>

        <Text fontSize="small" color="neutral.500">
          Current living expenses: {formatCurrency(options.CurrentLivingExpensesAmount)} per year
        </Text>
      </Box>

      <Box display="flex" flexDirection="column" gridGap="16" px="16" pb="32">
        <Controller
          name="PercentOfLivingExpenses"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <Select
              {...field}
              label="Living Expenses"
              placeholder="Select amount"
              options={livingExpensesOptions}
              value={livingExpensesOptions.find(option => option.value === value)}
              onChange={option => onChange(option?.value ?? RetirementExpenses.E100)}
              formVariant={errors.PercentOfLivingExpenses ? 'error' : undefined}
              helperText={errors.PercentOfLivingExpenses?.message}
              isPortal
            />
          )}
        />

        {showAmountInput && (
          <Controller
            control={control}
            name="LivingExpenseAmount"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Annual Living Expenses"
                decimalScale={0}
                allowNegative={false}
                leftIcon="far fa-dollar-sign"
                innerText="/year"
                formVariant={errors.LivingExpenseAmount ? 'error' : undefined}
                helperText={errors.LivingExpenseAmount?.message}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
              />
            )}
          />
        )}
      </Box>
    </>
  );
};
